import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Avatar,
  Button,
  Checkbox,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Spin,
  Card,
  Switch,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  BulbOutlined,
  ThunderboltOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CrownOutlined,
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  ExclamationCircleOutlined,
  LockOutlined,
  MailOutlined,
  MoonOutlined,
  PlusOutlined,
  QrcodeOutlined,
  RightOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  SecurityScanOutlined,
  StarOutlined,
  SunOutlined,
  TeamOutlined,
  UnlockOutlined,
  UserAddOutlined,
  UserOutlined,
  BellOutlined,
  PhoneOutlined,
  CameraOutlined,
  CloudSyncOutlined,
  OrderedListOutlined,
  BoxPlotOutlined,
  CheckSquareOutlined,
  CarOutlined,
  EditOutlined as EditIconOutlined,
  StockOutlined,
  InboxOutlined,
  SwapOutlined,
  BarChartOutlined,
  AlertOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { Triangle } from 'react-loader-spinner';
import moment from 'moment';
import config from '../config';
import './SettingsPage.css';

const { Title, Text } = Typography;
const { Option } = Select;

const defaultPermissions = {
  dashboardAccess: true,
  dashboardPackingLiveUpdates: false,
  syncAccess: false,
  ordersAccess: true,
  packingAccess: true,
  deliveryAccess: false,
  productsAccess: false,
  settingsAccess: false,
};

const PERMISSION_META = {
  dashboardAccess: { icon: <UserOutlined />, label: 'Dashboard' },
  dashboardPackingLiveUpdates: { icon: <ThunderboltOutlined />, label: 'Live Packing Updates' },
  syncAccess: { icon: <CloudSyncOutlined />, label: 'Orders Sync' },
  ordersAccess: { icon: <OrderedListOutlined />, label: 'Orders' },
  packingAccess: { icon: <BoxPlotOutlined />, label: 'Packing' },
  pickingAccess: { icon: <CheckSquareOutlined />, label: 'Picking' },
  deliveryAccess: { icon: <CarOutlined />, label: 'Delivery' },
  productsAccess: { icon: <EditIconOutlined />, label: 'Products' },
  stockAssessmentAccess: { icon: <StockOutlined />, label: 'Stock Assessment' },
  settingsAccess: { icon: <EditOutlined />, label: 'Settings' },
  rtoInboundAccess: { icon: <InboxOutlined />, label: 'RTO Inbound' },
  replacementsAccess: { icon: <SwapOutlined />, label: 'Replacements' },
  rtoInventoryAccess: { icon: <BoxPlotOutlined />, label: 'RTO Inventory' },
  rtoReportingAccess: { icon: <BarChartOutlined />, label: 'RTO Reporting' },
  escalationsAccess: { icon: <AlertOutlined />, label: 'Escalations & Claims' },
};

const SettingsPage = ({ token, userName, darkTheme, toggleTheme }) => {
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    role: 'Product Designer',
    phone: '',
    location: '',
    joinDate: '',
    lastActive: '',
    is2FAEnabled: false
  });
  const [users, setUsers] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [newUser, setNewUser] = useState({
    name: '', email: '', password: '', role: 'user', permissions: { ...defaultPermissions },
  });
  const [editUser, setEditUser] = useState(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [passwordForm] = Form.useForm();
  const [profileForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  // 2FA States
  const [twoFAQR, setTwoFAQR] = useState(null);
  const [setupTwoFAModalVisible, setSetupTwoFAModalVisible] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [loadingTwoFA, setLoadingTwoFA] = useState(false);
  const [verify2FAModalVisible, setVerify2FAModalVisible] = useState(false);
  const [adminUser2FAModal, setAdminUser2FAModal] = useState(null);
  const [adminUser2FAQR, setAdminUser2FAQR] = useState(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [openPackingSessions, setOpenPackingSessions] = useState([]);
  const [openSessionsLoading, setOpenSessionsLoading] = useState(false);
  const [releaseSession, setReleaseSession] = useState(null);
  const [releaseReason, setReleaseReason] = useState('');
  const [releaseBusy, setReleaseBusy] = useState(false);
  const [loginActivityUser, setLoginActivityUser] = useState(null);
  const [loginActivity, setLoginActivity] = useState([]);
  const [loginActivityVisible, setLoginActivityVisible] = useState(false);
  const [loginActivityLoading, setLoginActivityLoading] = useState(false);

  // Preferences (local state only, no backend yet)
  const [preferences, setPreferences] = useState({
    theme: darkTheme ? 'dark' : 'light',
    emailOnRto: true,
    emailOnDamage: true,
    emailDailyReport: false,
    browserPush: true,
  });

  // Sync preferences.theme when darkTheme prop changes (e.g., from hero button toggle)
  useEffect(() => {
    setPreferences(prev => ({ ...prev, theme: darkTheme ? 'dark' : 'light' }));
  }, [darkTheme]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${config.apiBaseUrl}/api/profile`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => {
        const profileData = {
          ...res.data,
          joinDate: res.data.joinDate || '2024-01-15',
          lastActive: res.data.lastActive || new Date().toISOString(),
          is2FAEnabled: res.data.is2FAEnabled || false
        };
        setProfile(profileData);
        setIsAdmin(res.data.role === 'admin');
        profileForm.setFieldsValue(profileData);
      })
      .catch(() => {
        messageApi.error('Failed to fetch profile');
      })
      .finally(() => {
        if (!isAdmin) {
          setLoading(false);
        }
      });

    if (isAdmin) {
      axios.get(`${config.apiBaseUrl}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then((res) => setUsers(res.data))
        .catch(() => messageApi.error('Session expired. Please log in again.'))
        .finally(() => setLoading(false));

      setOpenSessionsLoading(true);
      axios.get(`${config.apiBaseUrl}/packing/admin/open-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then((res) => setOpenPackingSessions(Array.isArray(res.data.sessions) ? res.data.sessions : []))
        .catch(() => messageApi.error('Failed to fetch open packing sessions.'))
        .finally(() => setOpenSessionsLoading(false));
    }
  }, [token, isAdmin, profileForm, messageApi]);

  const handleReleaseSession = async () => {
    if (!releaseSession || !releaseReason.trim()) {
      messageApi.warning('Enter a reason before closing the session.');
      return;
    }
    setReleaseBusy(true);
    try {
      await axios.post(
        `${config.apiBaseUrl}/packing/order-session/${encodeURIComponent(releaseSession.orderID)}/release`,
        { reason: releaseReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOpenPackingSessions((previous) => previous.filter((session) => session.orderID !== releaseSession.orderID));
      setReleaseSession(null);
      setReleaseReason('');
      messageApi.success('Packing session closed and released.');
    } catch (error) {
      messageApi.error(error.response?.data?.message || 'Could not close the packing session.');
    } finally {
      setReleaseBusy(false);
    }
  };

  const handleViewLoginActivity = async (user) => {
    setLoginActivityUser(user);
    setLoginActivity([]);
    setLoginActivityVisible(true);
    setLoginActivityLoading(true);
    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/api/admin/users/${user._id}/login-activity`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLoginActivity(Array.isArray(response.data.activity) ? response.data.activity : []);
    } catch (error) {
      messageApi.error(error.response?.data?.message || 'Failed to fetch login activity');
    } finally {
      setLoginActivityLoading(false);
    }
  };

  const handleProfileUpdate = (values) => {
    axios.put(`${config.apiBaseUrl}/api/profile`, values, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(() => {
        messageApi.success('Profile updated successfully');
        setProfile({ ...profile, ...values });
      })
      .catch(() => messageApi.error('Failed to update profile'));
  };

  const handlePasswordChange = (values) => {
    if (values.newPassword !== values.confirmPassword) {
      messageApi.error('New passwords do not match');
      return;
    }
    axios.put(`${config.apiBaseUrl}/api/change-password`, {
      currentPassword: values.currentPassword,
      newPassword: values.newPassword
    }, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        messageApi.success('Password updated successfully');
        passwordForm.resetFields();
      })
      .catch((err) => {
        messageApi.error(err.response?.data?.message || 'Failed to update password');
      });
  };

  const handleGenerate2FA = async () => {
    setLoadingTwoFA(true);
    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/api/auth/2fa/generate`,
        { userId: profile._id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTwoFAQR(response.data.qr);
      setSetupTwoFAModalVisible(true);
      messageApi.success('QR code generated. Scan with Microsoft Authenticator.');
    } catch (err) {
      messageApi.error(err.response?.data?.message || 'Failed to generate 2FA QR');
    } finally {
      setLoadingTwoFA(false);
    }
  };

  const handleVerify2FASetup = async () => {
    if (!totpCode || totpCode.length !== 6) {
      messageApi.error('Enter a valid 6-digit code');
      return;
    }
    setLoadingTwoFA(true);
    try {
      await axios.post(
        `${config.apiBaseUrl}/api/auth/2fa/verify-setup`,
        { userId: profile._id, token: totpCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      messageApi.success('Two-factor authentication enabled!');
      setProfile({ ...profile, is2FAEnabled: true });
      setSetupTwoFAModalVisible(false);
      setTotpCode('');
      setTwoFAQR(null);
    } catch (err) {
      messageApi.error(err.response?.data?.message || 'Failed to verify 2FA code');
    } finally {
      setLoadingTwoFA(false);
    }
  };

  const handleDisable2FA = async () => {
    try {
      await axios.post(
        `${config.apiBaseUrl}/api/auth/2fa/disable`,
        { userId: profile._id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      messageApi.success('Two-factor authentication disabled');
      setProfile({ ...profile, is2FAEnabled: false });
    } catch (err) {
      messageApi.error(err.response?.data?.message || 'Failed to disable 2FA');
    }
  };

  const handleAdminGenerate2FA = async (user) => {
    setLoadingTwoFA(true);
    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/api/auth/2fa/generate`,
        { userId: user._id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAdminUser2FAQR(response.data.qr);
      setAdminUser2FAModal(user);
      messageApi.success('QR code generated for user');
    } catch (err) {
      messageApi.error(err.response?.data?.message || 'Failed to generate 2FA QR');
    } finally {
      setLoadingTwoFA(false);
    }
  };

  const handleAdminDisable2FA = async (userId) => {
    try {
      await axios.post(
        `${config.apiBaseUrl}/api/auth/2fa/disable`,
        { userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      messageApi.success('Two-factor authentication disabled for user');
      setUsers(users.map((u) => (u._id === userId ? { ...u, is2FAEnabled: false } : u)));
    } catch (err) {
      messageApi.error(err.response?.data?.message || 'Failed to disable 2FA');
    }
  };

  const handleCreateUser = () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      messageApi.error('Name, email, and password are required');
      return;
    }
    axios.post(`${config.apiBaseUrl}/api/admin/users`, newUser, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => {
        messageApi.success('User created successfully');
        setUsers([...users, res.data.user]);
        setNewUser({ name: '', email: '', password: '', role: 'user', permissions: { ...defaultPermissions } });
        setCreateModalVisible(false);
      })
      .catch((err) => {
        messageApi.error(err.response?.data?.message || 'Failed to create user');
      });
  };

  const handleEditUser = (user) => {
    setEditUser({ ...user, permissions: { ...defaultPermissions, ...(user.permissions || {}) } });
    setEditModalVisible(true);
  };

  const handleUpdateUser = () => {
    axios.put(`${config.apiBaseUrl}/api/admin/users/${editUser._id}`, editUser, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => {
        messageApi.success('User updated successfully');
        setUsers(users.map((u) => (u._id === editUser._id ? res.data.user : u)));
        setEditModalVisible(false);
        setEditUser(null);
      })
      .catch((err) => {
        messageApi.error(err.response?.data?.message || 'Failed to update user');
      });
  };

  const handleDeleteUser = (userId) => {
    axios.delete(`${config.apiBaseUrl}/api/admin/users/${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(() => {
        messageApi.success('User deleted successfully');
        setUsers(users.filter((u) => u._id !== userId));
      })
      .catch((err) => {
        messageApi.error(err.response?.data?.message || 'Failed to delete user');
      });
  };

  const getInitials = (name) =>
    name ? name.split(' ').map((n) => n[0]).join('').toUpperCase() : 'U';

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return moment(dateString).format('DD MMM YYYY');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    return moment(dateString).format('DD MMM YYYY, HH:mm');
  };

  const enabledPermissionsCount = useMemo(
    () => (profile && profile.permissions ? Object.values(profile.permissions).filter(Boolean).length : 0),
    [profile]
  );

  if (loading) {
    return (
      <div className="loading-container">
        <Triangle visible height="80" width="80" color="#0f6b3c" ariaLabel="triangle-loading" />
        <Text style={{ marginTop: 16, color: '#0f6b3c' }}>Loading Settings...</Text>
      </div>
    );
  }

  // Stat cards for the top row (colours now come from CSS via the tone-* class)
  const statCards = [
    {
      key: 'account',
      label: 'Account status',
      value: 'Active',
      icon: <CheckCircleOutlined />,
      gradient: '',
      isPill: true,
    },
    {
      key: 'perms',
      label: 'Permissions enabled',
      value: enabledPermissionsCount,
      icon: <SafetyCertificateOutlined />,
      gradient: '',
      suffix: 'of 9',
    },
    {
      key: '2fa',
      label: 'Two-factor auth',
      value: profile.is2FAEnabled ? 'On' : 'Off',
      icon: <LockOutlined />,
      gradient: '',
      isPill: true,
    },
    {
      key: 'team',
      label: 'Team members',
      value: isAdmin ? users.length : '—',
      icon: <TeamOutlined />,
      gradient: '',
    },
  ];

  const filteredUsers = users.filter(
    (u) =>
      u.name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  // Tabs configuration
  const tabItems = [
    {
      key: 'profile',
      label: (
        <span>
          <UserOutlined /> Profile
        </span>
      ),
      children: (
        <div className="settings-tab-body">
          <Title level={4} className="settings-section-title">Profile information</Title>
          <Text className="settings-section-sub">Update your name and contact details.</Text>
          <Form
            form={profileForm}
            layout="vertical"
            onFinish={handleProfileUpdate}
          >
            <div className="settings-form-grid">
              <Form.Item
                name="name"
                label="Full name"
                rules={[{ required: true, message: 'Please enter your name' }]}
              >
                <Input prefix={<UserOutlined className="input-icon" />} placeholder="Enter your full name" className="custom-input" size="large" />
              </Form.Item>
              <Form.Item name="email" label="Email address">
                <Input prefix={<MailOutlined className="input-icon" />} placeholder="Your email" className="custom-input" size="large" disabled />
              </Form.Item>
            </div>
            <Form.Item>
              <Button type="primary" htmlType="submit" size="large" className="save-button" icon={<CheckCircleOutlined />}>
                Save changes
              </Button>
            </Form.Item>
          </Form>
        </div>
      ),
    },
    {
      key: 'security',
      label: (
        <span>
          <SecurityScanOutlined /> Security
        </span>
      ),
      children: (
        <div className="settings-tab-body">
          <div className="settings-two-col">
            <div className="settings-block">
              <Title level={4} className="settings-section-title">Change password</Title>
              <Text className="settings-section-sub">Update your password to keep your account secure.</Text>
              <Form form={passwordForm} layout="vertical" onFinish={handlePasswordChange}>
                <Form.Item name="currentPassword" label="Current password" rules={[{ required: true }]}>
                  <Input.Password prefix={<LockOutlined className="input-icon" />} placeholder="Enter current password" className="custom-input" size="large" />
                </Form.Item>
                <Form.Item name="newPassword" label="New password" rules={[{ required: true, min: 8 }]}>
                  <Input.Password prefix={<LockOutlined className="input-icon" />} placeholder="Enter new password" className="custom-input" size="large" />
                </Form.Item>
                <Form.Item name="confirmPassword" label="Confirm new password" rules={[{ required: true }]}>
                  <Input.Password prefix={<LockOutlined className="input-icon" />} placeholder="Confirm new password" className="custom-input" size="large" />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" size="large" className="save-button" icon={<CheckCircleOutlined />}>
                    Update password
                  </Button>
                </Form.Item>
              </Form>
            </div>

            <div className="settings-block">
              <Title level={4} className="settings-section-title">Two-factor authentication (2FA)</Title>
              <Text className="settings-section-sub">Add an extra layer of security using Microsoft Authenticator.</Text>

              <div className={`settings-2fa-card ${profile.is2FAEnabled ? 'enabled' : 'disabled'}`}>
                <div className="settings-2fa-icon">
                  {profile.is2FAEnabled ? <SafetyCertificateOutlined /> : <ExclamationCircleOutlined />}
                </div>
                <div className="settings-2fa-info">
                  <div className="settings-2fa-info-title">Status: {profile.is2FAEnabled ? 'Enabled' : 'Disabled'}</div>
                  <div className="settings-2fa-info-sub">
                    {profile.is2FAEnabled
                      ? 'Your account is protected with two-factor authentication.'
                      : 'Enable 2FA to add an extra layer of security.'}
                  </div>
                </div>
                {profile.is2FAEnabled ? (
                  <Popconfirm
                    title="Disable 2FA"
                    description="Are you sure you want to disable two-factor authentication?"
                    onConfirm={handleDisable2FA}
                    okText="Yes, disable"
                    cancelText="Cancel"
                  >
                    <Button danger icon={<LockOutlined />}>Disable 2FA</Button>
                  </Popconfirm>
                ) : (
                  <Button type="primary" icon={<QrcodeOutlined />} onClick={handleGenerate2FA} loading={loadingTwoFA}>
                    Enable 2FA
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'preferences',
      label: (
        <span>
          <BulbOutlined /> Preferences
        </span>
      ),
      children: (
        <div className="settings-tab-body">
          <div className="settings-pref-grid">
            <div className="preferences-section">
              <h4><BulbOutlined /> Appearance</h4>
              <div className="preference-row">
                <div className="preference-row-label">
                  <strong>Theme</strong>
                  <small>Choose how the dashboard looks across the app</small>
                </div>
                <div className="preference-row-control">
                  <Segmented
                    value={preferences.theme}
                    onChange={(v) => {
                      setPreferences({ ...preferences, theme: v });
                      if ((v === 'dark') !== darkTheme) toggleTheme();
                    }}
                    options={[
                      { label: <span><SunOutlined /> Light</span>, value: 'light' },
                      { label: <span><MoonOutlined /> Dark</span>, value: 'dark' },
                    ]}
                  />
                </div>
              </div>
            </div>

            <div className="preferences-section">
              <h4><BellOutlined /> Notifications</h4>
              <div className="preference-row">
                <div className="preference-row-label">
                  <strong>Email me when a new RTO is received</strong>
                  <small>Get notified every time a return inspection is logged</small>
                </div>
                <div className="preference-row-control">
                  <Switch checked={preferences.emailOnRto} onChange={(c) => setPreferences({ ...preferences, emailOnRto: c })} />
                </div>
              </div>
              <div className="preference-row">
                <div className="preference-row-label">
                  <strong>Email me when a product is marked damaged</strong>
                  <small>Instant alerts for damaged / scrapped products</small>
                </div>
                <div className="preference-row-control">
                  <Switch checked={preferences.emailOnDamage} onChange={(c) => setPreferences({ ...preferences, emailOnDamage: c })} />
                </div>
              </div>
              <div className="preference-row">
                <div className="preference-row-label">
                  <strong>Daily report email</strong>
                  <small>A morning summary of yesterday's RTOs</small>
                </div>
                <div className="preference-row-control">
                  <Switch checked={preferences.emailDailyReport} onChange={(c) => setPreferences({ ...preferences, emailDailyReport: c })} />
                </div>
              </div>
              <div className="preference-row">
                <div className="preference-row-label">
                  <strong>Browser push notifications</strong>
                  <small>Show desktop notifications for important events</small>
                </div>
                <div className="preference-row-control">
                  <Switch checked={preferences.browserPush} onChange={(c) => setPreferences({ ...preferences, browserPush: c })} />
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    ...(isAdmin
      ? [
          {
            key: 'users',
            label: (
              <span>
                <TeamOutlined /> User management
                <span className="settings-tab-count">{users.length}</span>
              </span>
            ),
            children: (
              <div className="settings-tab-body">
                <div className="users-header">
                  <div>
                    <Title level={4} className="settings-section-title" style={{ marginBottom: 0 }}>Team members</Title>
                    <Text className="settings-section-sub" style={{ marginBottom: 0 }}>Manage user accounts and permissions.</Text>
                  </div>
                  <div className="users-toolbar">
                    <Input
                      placeholder="Search users by name or email..."
                      prefix={<SearchOutlined />}
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                      className="users-search"
                      size="large"
                      allowClear
                    />
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setCreateModalVisible(true)}
                      className="create-user-btn"
                      size="large"
                    >
                      Add user
                    </Button>
                  </div>
                </div>

                <div className="users-list">
                  {filteredUsers.length === 0 ? (
                    <Empty description="No users match your search" />
                  ) : (
                    filteredUsers.map((user) => (
                      <div key={user._id} className="user-row">
                        <Avatar size={48} className="user-row-avatar">
                          {getInitials(user.name)}
                        </Avatar>
                        <div className="user-row-info">
                          <div className="user-row-info-name">
                            {user.name}
                            {user.role === 'admin' && (
                              <Tag icon={<CrownOutlined />} color="gold">Admin</Tag>
                            )}
                            {user.is2FAEnabled && (
                              <Tag icon={<SafetyCertificateOutlined />} color="green">2FA</Tag>
                            )}
                            {user.isActive && <Tag color="blue">Active</Tag>}
                          </div>
                          <div className="user-row-info-email">{user.email}</div>
                          <div className="user-row-info-stats">
                            {Object.values(user.permissions || {}).filter(Boolean).length} permissions active
                            {user.lastLoginAt && ` · Last login ${moment(user.lastLoginAt).format('DD MMM YYYY, hh:mm A')}`}
                          </div>
                        </div>
                        <div className="user-row-actions">
                          {user.is2FAEnabled && (
                            <Tooltip title="Disable 2FA">
                              <Popconfirm
                                title="Disable 2FA"
                                description="Disable 2FA for this user?"
                                onConfirm={() => handleAdminDisable2FA(user._id)}
                                okText="Yes"
                                cancelText="No"
                              >
                                <Button type="text" danger icon={<UnlockOutlined />} className="user-row-action disable" />
                              </Popconfirm>
                            </Tooltip>
                          )}
                          <Tooltip title="View login activity">
                            <Button
                              type="text"
                              icon={<HistoryOutlined />}
                              className="user-row-action edit"
                              onClick={() => handleViewLoginActivity(user)}
                            />
                          </Tooltip>
                          <Tooltip title="Edit user">
                            <Button type="text" icon={<EditOutlined />} className="user-row-action edit" onClick={() => handleEditUser(user)} />
                          </Tooltip>
                          <Tooltip title="Delete user">
                            <Popconfirm
                              title="Delete user"
                              description="Are you sure you want to delete this user?"
                              onConfirm={() => handleDeleteUser(user._id)}
                              okText="Yes"
                              cancelText="No"
                            >
                              <Button type="text" danger icon={<DeleteOutlined />} className="user-row-action delete" />
                            </Popconfirm>
                          </Tooltip>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ),
          },
          {
            key: 'open-sessions',
            label: (
              <span>
                <UnlockOutlined /> Open packing sessions
                <span className="settings-tab-count">{openPackingSessions.length}</span>
              </span>
            ),
            children: (
              <div className="settings-tab-body">
                <div className="users-header">
                  <div>
                    <Title level={4} className="settings-section-title" style={{ marginBottom: 0 }}>Open packing sessions</Title>
                    <Text className="settings-section-sub" style={{ marginBottom: 0 }}>Review orders still owned by an active packing session and release them when required.</Text>
                  </div>
                  <Button onClick={() => {
                    setOpenSessionsLoading(true);
                    axios.get(`${config.apiBaseUrl}/packing/admin/open-sessions`, { headers: { Authorization: `Bearer ${token}` } })
                      .then((res) => setOpenPackingSessions(Array.isArray(res.data.sessions) ? res.data.sessions : []))
                      .finally(() => setOpenSessionsLoading(false));
                  }}>Refresh</Button>
                </div>
                {openSessionsLoading ? (
                  <div className="settings-session-empty"><Spin /></div>
                ) : openPackingSessions.length === 0 ? (
                  <Empty description="No open packing sessions" />
                ) : (
                  <div className="settings-open-sessions-list">
                    {openPackingSessions.map((session) => (
                      <div className="settings-open-session-row" key={session.orderID}>
                        <div className="settings-open-session-main">
                          <strong>{session.orderID}</strong>
                          <span>{session.customerName}</span>
                        </div>
                        <div className="settings-open-session-meta">
                          <span>Opened by <strong>{session.openedByName}</strong></span>
                          <span>{session.openedAt ? moment(session.openedAt).format('DD MMM YYYY, hh:mm A') : 'Time unavailable'}</span>
                        </div>
                        <Button danger onClick={() => { setReleaseSession(session); setReleaseReason(''); }}>
                          Close session
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className={`settings-page-container ${darkTheme ? 'dark-mode' : 'light-mode'}`}>
      {contextHolder}

      <div className="settings-shell">
        <nav className="settings-breadcrumb" aria-label="Breadcrumb">
          <span>Packing Dashboard</span>
          <RightOutlined className="settings-breadcrumb-sep" />
          <span className="settings-breadcrumb-current">Settings</span>
        </nav>

        {/* HEADER */}
        <div className="settings-hero">
          <div className="settings-hero-left">
            <span className="settings-hero-eyebrow">Account</span>
            <Title level={2} className="settings-hero-title">Settings & Preferences</Title>
            <Text className="settings-hero-sub">Manage your account settings and user preferences.</Text>
          </div>

          <div className="settings-hero-center">
            <div className="settings-hero-status-label">Account status</div>
            <div className="settings-hero-status-row">
              <span><CheckCircleOutlined /> Active</span>
              <span><SafetyCertificateOutlined /> {profile.is2FAEnabled ? '2FA On' : '2FA Off'}</span>
            </div>
          </div>

          <div className="settings-hero-actions">
            <Button
              shape="circle"
              icon={darkTheme ? <SunOutlined /> : <MoonOutlined />}
              onClick={typeof toggleTheme === 'function' ? toggleTheme : undefined}
              aria-label="Toggle theme"
            />
            <Button type="primary" icon={<UserOutlined />}>My account</Button>
          </div>
        </div>

        {/* STAT CARDS */}
        <div className="settings-stat-grid">
          {statCards.map((s) => (
            <div
              key={s.key}
              className={`settings-stat-card tone-${s.key}${s.key === '2fa' && !profile.is2FAEnabled ? ' warn' : ''}`}
            >
              <div className="settings-stat-head">
                <span className="settings-stat-label">{s.label}</span>
                <span className="settings-stat-icon">{s.icon}</span>
              </div>
              <div className="settings-stat-value">
                {s.isPill ? (
                  <span className="settings-stat-pill">{s.value}</span>
                ) : (
                  <>
                    {s.value}
                    {s.suffix && <span className="settings-stat-suffix">{s.suffix}</span>}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* MAIN LAYOUT: PROFILE RAIL + TABS */}
        <div className="settings-layout">
          {/* Left profile rail */}
          <aside className="settings-profile-rail">
            <div className="settings-profile-card">
              <div className="settings-profile-avatar-wrap">
                <Avatar
                  size={102}
                  className="settings-profile-avatar"
                  icon={<UserOutlined />}
                >
                  {getInitials(profile.name)}
                </Avatar>
                <div className="settings-profile-avatar-btn" title="Change avatar">
                  <CameraOutlined />
                </div>
              </div>

              <Title level={3} className="settings-profile-name">{profile.name || 'User Name'}</Title>
              <div>
                <span className={`settings-profile-role ${isAdmin ? 'admin' : 'user'}`}>
                  {isAdmin ? <CrownOutlined /> : <StarOutlined />}
                  {isAdmin ? 'Administrator' : (profile.role || 'User')}
                </span>
              </div>

              <div className="settings-profile-divider" />

              <div className="settings-profile-info">
                <div className="settings-profile-info-row">
                  <MailOutlined /> <span>{profile.email || '—'}</span>
                </div>
                {profile.phone && (
                  <div className="settings-profile-info-row">
                    <PhoneOutlined /> <span>{profile.phone}</span>
                  </div>
                )}
                {profile.location && (
                  <div className="settings-profile-info-row">
                    <EnvironmentOutlined /> <span>{profile.location}</span>
                  </div>
                )}
                <div className="settings-profile-info-row">
                  <CalendarOutlined /> <span>Joined {formatDate(profile.joinDate)}</span>
                </div>
                <div className="settings-profile-info-row">
                  <span>Last active {formatDateTime(profile.lastActive)}</span>
                </div>
              </div>

              <div className="settings-profile-stats">
                <div className="settings-profile-stat">
                  <span className="settings-profile-stat-value">{isAdmin ? users.length : 0}</span>
                  <span className="settings-profile-stat-label">Team members</span>
                </div>
                <div className="settings-profile-stat">
                  <span className="settings-profile-stat-value">{enabledPermissionsCount}</span>
                  <span className="settings-profile-stat-label">Permissions</span>
                </div>
              </div>
            </div>

            <div className="settings-security-card">
              <div className="settings-security-card-title">
                <SafetyCertificateOutlined /> Security status
              </div>
              <div className="settings-security-item">
                <span>
                  <CheckCircleOutlined /> Password strength
                </span>
                <span className="settings-security-status on">Strong</span>
              </div>
              <div className="settings-security-item">
                <span>
                  {profile.is2FAEnabled ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
                  Two-factor authentication
                </span>
                <span className={`settings-security-status ${profile.is2FAEnabled ? 'on' : 'off'}`}>
                  {profile.is2FAEnabled ? 'On' : 'Off'}
                </span>
              </div>
              <div className="settings-security-item">
                <span>
                  <CheckCircleOutlined /> Email verified
                </span>
                <span className="settings-security-status on">Yes</span>
              </div>
            </div>
          </aside>

          {/* Right: tabs */}
          <Card className="settings-tabs-card" variant="borderless">
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              className="settings-tabs"
              items={tabItems}
            />
          </Card>
        </div>
      </div>

      <Modal
        title="Close packing session"
        open={!!releaseSession}
        onCancel={() => { if (!releaseBusy) { setReleaseSession(null); setReleaseReason(''); } }}
        onOk={handleReleaseSession}
        okText="Close session"
        okButtonProps={{ danger: true, loading: releaseBusy }}
        cancelButtonProps={{ disabled: releaseBusy }}
        className="user-modal"
      >
        <Text>
          This will release order <strong>{releaseSession?.orderID}</strong> from <strong>{releaseSession?.openedByName}</strong> so it can be opened again.
        </Text>
        <Input.TextArea
          value={releaseReason}
          onChange={(event) => setReleaseReason(event.target.value)}
          placeholder="Reason for closing this session"
          rows={4}
          style={{ marginTop: 16 }}
        />
      </Modal>

      <Modal
        title={<Space><HistoryOutlined /> Login activity · {loginActivityUser?.name || loginActivityUser?.email}</Space>}
        open={loginActivityVisible}
        onCancel={() => setLoginActivityVisible(false)}
        footer={null}
        width={720}
        className="user-modal"
      >
        {loginActivityLoading ? <Spin /> : loginActivity.length === 0 ? (
          <Empty description="No login activity recorded yet" />
        ) : (
          <div className="login-activity-list">
            {loginActivity.map((entry, index) => (
              <div className="login-activity-row" key={`${entry.loggedInAt}-${index}`}>
                <div>
                  <Text strong>{moment(entry.loggedInAt).format('DD MMM YYYY, hh:mm A')}</Text>
                  <Text type="secondary" className="login-activity-meta">
                    {entry.loginMethod === 'microsoft' ? 'Microsoft login' : 'Password / 2FA'}
                  </Text>
                </div>
                <div className="login-activity-details">
                  <Text type="secondary">IP: {entry.ipAddress || 'Unavailable'}</Text>
                  <Tooltip title={entry.userAgent || 'Unavailable'}>
                    <Text type="secondary">Browser details</Text>
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* CREATE USER MODAL */}
      <Modal
        title={
          <Space>
            <UserAddOutlined /> Create new user
          </Space>
        }
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        onOk={handleCreateUser}
        width={720}
        className="user-modal"
        okText="Create user"
        cancelText="Cancel"
      >
        <Form layout="vertical">
          <div className="settings-form-grid">
            <Form.Item label="Full name" required>
              <Input
                prefix={<UserOutlined className="input-icon" />}
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="Enter user name"
                className="custom-input"
                size="large"
              />
            </Form.Item>
            <Form.Item label="Email" required>
              <Input
                prefix={<MailOutlined className="input-icon" />}
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="Enter user email"
                className="custom-input"
                size="large"
              />
            </Form.Item>
          </div>
          <div className="settings-form-grid">
            <Form.Item label="Password" required>
              <Input.Password
                prefix={<LockOutlined className="input-icon" />}
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Enter password"
                className="custom-input"
                size="large"
              />
            </Form.Item>
            <Form.Item label="Role">
              <Select
                value={newUser.role}
                onChange={(value) => setNewUser({ ...newUser, role: value })}
                className="custom-select"
                size="large"
              >
                <Option value="user">User</Option>
                <Option value="admin">Administrator</Option>
              </Select>
            </Form.Item>
          </div>

          <div className="permissions-grid">
            {Object.entries(newUser.permissions).map(([key, value]) => {
              const meta = PERMISSION_META[key];
              return (
                <div key={key} className="permission-item">
                  <Checkbox
                    checked={value}
                    onChange={(e) =>
                      setNewUser({
                        ...newUser,
                        permissions: { ...newUser.permissions, [key]: e.target.checked },
                      })
                    }
                  >
                    <span className="permission-ico">{meta?.icon}</span>
                    {meta?.label || key}
                  </Checkbox>
                </div>
              );
            })}
          </div>
        </Form>
      </Modal>

      {/* EDIT USER MODAL */}
      <Modal
        title={<Space><EditOutlined /> Edit user</Space>}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={handleUpdateUser}
        width={720}
        className="user-modal"
        okText="Update user"
        cancelText="Cancel"
      >
        {editUser && (
          <Form layout="vertical">
            <div className="settings-form-grid">
              <Form.Item label="Full name">
                <Input
                  value={editUser.name}
                  onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                  className="custom-input"
                  size="large"
                />
              </Form.Item>
              <Form.Item label="Email">
                <Input
                  value={editUser.email}
                  className="custom-input"
                  size="large"
                  disabled
                />
              </Form.Item>
            </div>
            <Form.Item label="Role">
              <Select
                value={editUser.role}
                onChange={(v) => setEditUser({ ...editUser, role: v })}
                className="custom-select"
                size="large"
              >
                <Option value="user">User</Option>
                <Option value="admin">Administrator</Option>
              </Select>
            </Form.Item>
            <div className="permissions-grid">
              {Object.entries(editUser.permissions || {}).map(([key, value]) => {
                const meta = PERMISSION_META[key];
                return (
                  <div key={key} className="permission-item">
                    <Checkbox
                      checked={value}
                      onChange={(e) =>
                        setEditUser({
                          ...editUser,
                          permissions: { ...editUser.permissions, [key]: e.target.checked },
                        })
                      }
                    >
                      <span className="permission-ico">{meta?.icon}</span>
                      {meta?.label || key}
                    </Checkbox>
                  </div>
                );
              })}
            </div>
          </Form>
        )}
      </Modal>

      {/* SETUP 2FA MODAL - Show QR Code */}
      <Modal
        title={
          <Space>
            <QrcodeOutlined /> Enable Two-Factor Authentication
          </Space>
        }
        open={setupTwoFAModalVisible}
        onCancel={() => {
          setSetupTwoFAModalVisible(false);
          setTwoFAQR(null);
          setTotpCode('');
        }}
        footer={null}
        width={480}
        className="user-modal"
      >
        <div className="settings-2fa-modal">
          {twoFAQR ? (
            <>
              <Text className="settings-2fa-modal-text">
                Scan this QR code with Microsoft Authenticator
              </Text>
              <div className="settings-2fa-qr">
                <img src={twoFAQR} alt="2FA QR Code" />
              </div>
              <Form layout="vertical" className="settings-2fa-form">
                <Form.Item label="Enter 6-digit code from Authenticator" required>
                  <Input
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    size="large"
                    className="custom-input settings-totp-input"
                  />
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    block
                    size="large"
                    loading={loadingTwoFA}
                    onClick={handleVerify2FASetup}
                    className="save-button"
                    icon={<CheckCircleOutlined />}
                  >
                    Verify & Enable 2FA
                  </Button>
                </Form.Item>
              </Form>
            </>
          ) : (
            <Spin size="large" />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default SettingsPage;
